import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Field, Form, Formik } from 'formik';
import { get } from 'lodash';
import { Grid } from '@material-ui/core';
import { connect, useDispatch } from 'react-redux';
import { object, string } from 'yup';
import { createStructuredSelector } from 'reselect';
import { useTranslation } from '../../src/i18n';
import SubmitModalLayout from '../SubmitModalLayout';
import { emptySelectValue, TP } from '../../constants/consts';
import EditModeCard from '../EditModeCard';
import FormikTextField from '../Fields/TextField';
import { createEntity, getWholeList, useBufferValueAction } from '../../containers/App/actions';
import SelectField from '../Fields/SelectField';
import { COUNTRIES_ENDPOINT, REGIONS } from '../../constants/endpointConstants';
import { getRegionsAction } from '../../store/actions';
import { selectRegions } from '../../store/selectors';
import { selectBufferValueData } from '../../containers/App/selectors';

const useStyles = makeStyles(theme => ({
  form: {
    margin: theme.spacing(2),
  },
  fieldsBox: {
    padding: theme.spacing(2),
  },
  scrollable: {
    minHeight: '60vh',
  },
  select: {
    maxWidth: '360px',
  },
  codeSelect: {
    maxWidth: '180px',
  },
}));

const countryFields = {
  name: {
    name: 'name',
    label: `${TP}.m_COUNTRY`,
  },
  phoneCode: {
    name: 'phoneCode',
    label: `${TP}.FN_PHONE_CODE`,
  },
  regions: {
    name: 'regions',
    label: `${TP}.FN_REGION`,
  },
};

const { name, phoneCode, regions } = countryFields;

const AddNewCountryModal = ({ onClose, countryValue, regionsOptions, createdCountryData }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formRef = React.createRef(null);

  React.useEffect(() => {
    if (!regionsOptions.length) {
      dispatch(
        getWholeList({
          endpoint: REGIONS,
          sagaRoutine: getRegionsAction,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialValues = React.useCallback(() => {
    const region = regionsOptions.find(option => option?.value === 1); // world reagion
    return {
      [name.name]: countryValue,
      [phoneCode.name]: '',
      [regions.name]: region === -1 ? regionsOptions[0] || emptySelectValue : region,
    };
  }, [countryValue, regionsOptions]);

  const getRandomDigits = () => {
    let randDigits = '';
    const digits = '01234567890';
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < 2; i++) {
      randDigits += digits.charAt(Math.floor(Math.random() * digits.length));
    }
    return randDigits;
  };

  const getRandomCountryCode = () => {
    let randLetters = '';
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < 2; i++) {
      randLetters += letters.charAt(Math.floor(Math.random() * letters.length));
    }
    return `${randLetters}${getRandomDigits()}`;
  };

  const createCountry = values => {
    dispatch(
      createEntity({
        endpoint: COUNTRIES_ENDPOINT,
        sagaRoutine: useBufferValueAction,
        params: {
          name: get(values, name.name),
          countryCode: getRandomCountryCode(),
          phoneCode: values[phoneCode.name]?.value || getRandomDigits(),
          ...(values[regions.name]?.value ? { regions: [{ id: values[regions.name].value }] } : {}),
        },
        successMessage: t(`${TP}.SUCCESS_CREATING_COUNTRY`),
        callback: createdCountry => {
          onClose(createdCountry);
        },
      }),
    );
  };

  return (
    <SubmitModalLayout
      isOpened
      onClose={onClose}
      title1={t(`${TP}.FN_ADD_NEW_COUNTRY`)}
      submitFunc={() => {
        get(formRef, 'current.submitForm', () => {})();
      }}
      className={classes.scrollable}
      isLoading={createdCountryData?.loading}
    >
      <EditModeCard className={classes.form}>
        <Formik
          initialValues={initialValues()}
          onSubmit={createCountry}
          innerRef={formRef}
          validationSchema={AddNewCountrySchema}
          enableReinitialize
        >
          {() => (
            <Form className={classes.scrollable}>
              <Grid container spacing={4} className={classes.fieldsBox} alignItems="flex-start">
                <Grid item lg={5} sm={7} xs={12} classes={{ root: classes.select }}>
                  <Field name={name.name} component={FormikTextField} label={t(name.label)} />
                </Grid>
                <Grid item lg={2} sm={3} xs={12} classes={{ root: classes.codeSelect }}>
                  <Field name={phoneCode.name} component={FormikTextField} label={t(phoneCode.label)} type="number" />
                </Grid>
                <Grid item lg={5} sm={7} xs={12} classes={{ root: classes.select }}>
                  <Field
                    name={regions.name}
                    component={SelectField}
                    label={t(regions.label)}
                    options={regionsOptions}
                    staticLabel
                  />
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </EditModeCard>
    </SubmitModalLayout>
  );
};

AddNewCountryModal.propTypes = {
  onClose: PropTypes.func,
  countryValue: PropTypes.string,
  regionsOptions: PropTypes.array,
};

const mapStateToProps = createStructuredSelector({
  regionsOptions: selectRegions(),
  createdCountryData: selectBufferValueData(),
});

export default connect(mapStateToProps, null)(AddNewCountryModal);

const AddNewCountrySchema = () =>
  object().shape({
    [name.name]: string()
      .nullable()
      .required(`${TP}.FN_REQUIRED_FIELD`),
    [phoneCode.name]: string()
      .nullable()
      .required(`${TP}.FN_REQUIRED_FIELD`),
  });
